/**
 * Session Manager برای SyncPlay
 * مدیریت session با localStorage + encrypt ساده
 */

const SESSION_KEY = 'syncplay_session';
const USERNAME_KEY = 'syncplay_username';
const SESSION_EXPIRY_HOURS = 24;

class SessionManager {
  constructor() {
    this.session = null;
    this.initialize();
  }

  /**
   * راه‌اندازی اولیه - لود session از localStorage
   */
  initialize() {
    try {
      const storedSession = localStorage.getItem(SESSION_KEY);
      
      if (storedSession) {
        const parsed = JSON.parse(storedSession);
        
        // چک کردن expiry
        if (this.isSessionValid(parsed)) {
          this.session = parsed;
          this.updateLastActivity();
          return;
        } else {
          this.clearSession();
        }
      }
      
      // اگه session وجود نداشت یا expired بود
      this.createNewSession();
    } catch (error) {
      this.createNewSession();
    }
  }

  /**
   * ساخت session جدید
   */
  createNewSession(userData = {}) {
    const sessionId = this.generateSessionId();
    const now = Date.now();
    
    this.session = {
      sessionId: sessionId,
      createdAt: now,
      lastActivity: now,
      userAgent: navigator.userAgent.substring(0, 100),
      tabId: this.generateTabId(),
      // اطلاعات کاربر
      user: {
        username: userData.username || this.getStoredUsername() || null,
        avatar: userData.avatar || null,
        role: userData.role || 'user',
        // می‌تونید فیلدهای بیشتری اضافه کنید
        preferences: {
          language: userData.language || navigator.language || 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          darkMode: userData.darkMode ?? true,
        }
      },
      // متادیتا
      metadata: {
        version: '1.0.0',
        platform: this.detectPlatform(),
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      }
    };
    
    this.saveSession();
    return this.session;
  }

  /**
   * آپدیت اطلاعات کاربر
   */
  updateUser(userData) {
    if (!this.session) {
      this.createNewSession(userData);
      return this.session;
    }
    
    this.session.user = {
      ...this.session.user,
      ...userData,
      // username جداگانه ذخیره بشه
      username: userData.username || this.session.user.username,
    };
    
    this.updateLastActivity();
    this.saveSession();
    
    // ذخیره username توی localStorage جداگانه برای دسترسی سریع
    if (userData.username) {
      this.storeUsername(userData.username);
    }
    
    return this.session;
  }

  /**
   * آپدیت preferences
   */
  updatePreferences(preferences) {
    if (!this.session) return;
    
    this.session.user.preferences = {
      ...this.session.user.preferences,
      ...preferences,
    };
    
    this.updateLastActivity();
    this.saveSession();
  }

  /**
   * به‌روزرسانی lastActivity
   */
  updateLastActivity() {
    if (this.session) {
      this.session.lastActivity = Date.now();
      this.saveSession();
    }
  }

  /**
   * گرفتن session جاری
   */
  getSession() {
    // auto-refresh اگه نزدیک به expiry هست
    if (this.session && !this.isSessionValid(this.session)) {
      this.refreshSession();
    }
    
    return this.session;
  }

  /**
   * گرفتن sessionId
   */
  getSessionId() {
    return this.session?.sessionId || null;
  }

  /**
   * گرفتن اطلاعات کاربر
   */
  getUser() {
    return this.session?.user || null;
  }

  /**
   * گرفتن username
   */
  getUsername() {
    return this.session?.user?.username || this.getStoredUsername() || null;
  }

  /**
   * رفرش session (تمدید بدون از دست دادن داده)
   */
  refreshSession() {
    if (!this.session) {
      return this.createNewSession();
    }
    
    const userData = { ...this.session.user };
    return this.createNewSession(userData);
  }

  /**
   * پاک کردن کامل session
   */
  clearSession() {
    this.session = null;
    localStorage.removeItem(SESSION_KEY);
  }

  /**
   * خروج کاربر (پاک کردن session + username)
   */
  logout() {
    this.clearSession();
    localStorage.removeItem(USERNAME_KEY);
  }

  // ─── Private Methods ────────────────────────

  /**
   * ذخیره session توی localStorage
   */
  saveSession() {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(this.session));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        this.cleanupOldSessions();
      }
    }
  }

  /**
   * بررسی اعتبار session
   */
  isSessionValid(session) {
    if (!session || !session.createdAt || !session.lastActivity) {
      return false;
    }
    
    const now = Date.now();
    const expiryTime = SESSION_EXPIRY_HOURS * 60 * 60 * 1000;
    
    // چک کردن elapsed time از created
    if (now - session.createdAt > expiryTime) {
      return false;
    }
    
    // چک کردن inactivity (2 ساعت)
    const inactivityExpiry = 2 * 60 * 60 * 1000;
    if (now - session.lastActivity > inactivityExpiry) {
      return false;
    }
    
    return true;
  }

  /**
   * ساخت شناسه یکتای session
   */
  generateSessionId() {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 10);
    const extraRandom = Math.random().toString(36).substring(2, 6);
    
    return `sess_${timestamp}_${randomPart}_${extraRandom}`;
  }

  /**
   * ساخت شناسه tab
   */
  generateTabId() {
    return `tab_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  }

  /**
   * ذخیره username جداگانه
   */
  storeUsername(username) {
    localStorage.setItem(USERNAME_KEY, username);
    // ذخیره توی کوکی هم برای بک‌اند
    document.cookie = `syncplay_username=${encodeURIComponent(username)}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
  }

  /**
   * گرفتن username ذخیره شده
   */
  getStoredUsername() {
    return localStorage.getItem(USERNAME_KEY);
  }

  /**
   * تشخیص platform
   */
  detectPlatform() {
    const ua = navigator.userAgent;
    if (/Android/i.test(ua)) return 'android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    if (/Mac/i.test(ua)) return 'macos';
    if (/Windows/i.test(ua)) return 'windows';
    if (/Linux/i.test(ua)) return 'linux';
    return 'unknown';
  }

  /**
   * پاکسازی session های قدیمی (در صورت پر شدن فضا)
   */
  cleanupOldSessions() {
    // فقط session فعلی رو نگه دار
    const currentSessionId = this.session?.sessionId;
    localStorage.removeItem(SESSION_KEY);
    
    if (currentSessionId) {
      this.saveSession();
    }
  }

  /**
   * گرفتن آمار session
   */
  getSessionStats() {
    if (!this.session) return null;
    
    const now = Date.now();
    return {
      sessionAge: Math.floor((now - this.session.createdAt) / 1000 / 60), // minutes
      lastActivity: Math.floor((now - this.session.lastActivity) / 1000), // seconds
      isValid: this.isSessionValid(this.session),
      tabId: this.session.tabId,
    };
  }
}

// Singleton instance
const sessionManager = new SessionManager();

export default sessionManager;